import React, { useRef, useState } from 'react';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import * as Yup from 'yup';
import { Form, Formik } from 'formik';

import closeIcon from '../../../../Assets/close-icon.svg';
import { serviceConsumer } from '../../../../network/ServiceConsumer';
import { handlePhotoUpload } from '../../../../helper/handleUploads';
import AddButton from '../../../AddButton/AddButton';
import Loader from '../../../Loader/Loader';
import CustomInput from '../CustomInput';
import { ShowToastMessage } from '../../../../helper/ShowToastMessage';
import { adjudicatorStatusOf } from '../../../AdjudicatorStatus/AdjudicatorStatus';
import JudgeRegionField, { listedInDirectory, regionOf } from '../JudgeRegionField';

const AddCollabModal = ({ addOrEdit, onClose, editCollab, getList }) => {
    const [imgError, setImgErr] = useState('');
    const [logo, setLogo] = useState(editCollab?.photo);
    const [upl, setUpl] = useState('');
    const [loading, setLoading] = useState(false);
    const [photoEdited, setPhotoEdited] = useState(false);
    const imgInputRef = useRef(null);

    const handleLOgo = () => {
        imgInputRef.current.click();
    };


    //Initial values for formik
    const newCollabInitialValues = {
        name: editCollab?.name || "",
        fname: editCollab?.fname || "",
        lname: editCollab?.lname || "",
        location: editCollab?.location || "",
        photo: editCollab?.photo || "",
        type: editCollab?.type || "",
        email: editCollab?.email || "",
        region: regionOf(editCollab),
    };
    // a judge in the CLRG directory keeps its region; anyone else needs one
    const listed = listedInDirectory(editCollab);
    // An adjudicator's email can change until they set up their account
    // (card 930d3ac0); saving it doesn't send anything - the invitation goes
    // out when they're booked, or from "Send invitation" in the list.
    const emailLocked = adjudicatorStatusOf(editCollab) === "active";

    const newCollabValidationSchema = Yup.object().shape({
        fname: Yup.string()
            .trim()
            .required('This field is required.')
            .max(50),
            lname: Yup.string()
            .trim()
            .required('This field is required.')
            .max(50),
        // an adjudicator has a region instead (the place shown comes from it)
        location: Yup.string().when('type', {
            is: 'adjudicator',
            then: (schema) => schema.notRequired(),
            otherwise: (schema) => schema.trim().required('This field is required.').max(50),
        }),
        region: Yup.string().when('type', {
            // required for a new judge; on edit it stays optional (an older record
            // without one can still be saved), as on the event set-up edit form;
            // a record becoming an adjudicator on edit is a new judge: required
            is: (type) => type === 'adjudicator' && !listed && (addOrEdit === "Add" || editCollab?.type !== 'adjudicator'),
            then: (schema) => schema.required('Choose a region.'),
            otherwise: (schema) => schema.notRequired(),
        }),
        type: Yup.string().required("This field is required"),
        // An email can be changed but not removed: the invitation needs it.
        // adjudicators only (the field is hidden for other types)
        email: Yup.string().when('type', {
            is: 'adjudicator',
            // function form: valid on yup 0.32 (this app) and required from yup 1
            then: (schema) => editCollab?.email
                ? schema.trim().email('Enter a valid email address.').required("An adjudicator's email can be changed but not removed.")
                : schema.trim().email('Enter a valid email address.'),
            otherwise: (schema) => schema.notRequired(),
        }),
    });

    const handleFileSelect = async (event) => {
        handlePhotoUpload(event, setLoading, setLogo, setUpl, setImgErr);
        setPhotoEdited(true);
    };

    const addCollab = async (values, formikHelpers) => {
        const payload = {
            // name: values.name,
            fname: values.fname,
            lname: values.lname,
            ...(values.type === "adjudicator"
                ? (!listed && values.region ? { region: values.region } : {})
                : { location: values.location }),
            type: values.type,
            photo: logo,
            ...(values.type === "adjudicator" && !emailLocked && values.email.trim() && values.email.trim() !== (editCollab?.email || "")
                ? { email: values.email.trim() }
                : {}),
        };
        try {
            setLoading(true);
            const url = addOrEdit === "Add" ? `${process.env.REACT_APP_BASE_URL}/people` : `${process.env.REACT_APP_BASE_URL}/people/${editCollab?._id}`;
            const response = addOrEdit === "Add" ? await serviceConsumer('POST', url, payload) : await serviceConsumer('PUT', url, payload);
            setLoading(false);
            if (response && response.success === false) {
                ShowToastMessage("error", response.message || "Something went wrong");
                return;
            }
            getList()
            onClose()
        } catch (error) {
            console.log(error)
            setLoading(false);
            ShowToastMessage("error", error?.messages || "Something went wrong");
        }
    };

    const closeModalHandler = () => {
        onClose()
    }

    const deleteLogoHandler = () => {
        setLogo("")
        setUpl("")
        setPhotoEdited(true);
        if (imgInputRef.current) {
            imgInputRef.current.value = null;
        }

    }

    const title = addOrEdit === "Add" ? "Add Collaborator" : "Edit Collaborator"

    const buttonName = addOrEdit == "Add" ? "Add Collaborator" : "Update"

    return (
        <>
            {loading && <Loader />}
            <Modal className="addDancerScroll" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', }} open={true}>
                <Box>
                    <div style={{ maxHeight: '100vh', padding: '5px 100vw', overflow: 'scroll', }}>
                        <div className={logo ? "editCollab" : "main-div-add-adj"} style={{ height: "auto" }}>
                            <div className="close-div mt-4 mb-1">
                                <img src={closeIcon} alt="close-icon" className="close-icon" onClick={closeModalHandler} />
                            </div>
                            <p className="color-subtitle text-center mb-0">{title}</p>
                            <div className="d-flex align-items-center justify-content-center pb-5" style={{ background: 'white,borderRadius: 14px;' }}>
                                <Formik
                                    initialValues={newCollabInitialValues}
                                    validationSchema={newCollabValidationSchema}
                                    onSubmit={addCollab}
                                >
                                    {(formik) => (
                                        <Form className="form form-label-right" autoComplete="off">
                                            {/* <CustomInput label="Full name" name="name" type="text" placeholder="Name" formik={formik} /> */}
                                            <CustomInput label="First name" name="fname" type="text" placeholder="First Name" formik={formik} />
                                            <CustomInput label="Last name" name="lname" type="text" placeholder="Last Name" formik={formik} />
                                            {formik.values.type !== "adjudicator" && <CustomInput label="Location" name="location" type="text" placeholder="Location" formik={formik} />}
                                            <div className="d-grid">
                                                <label className="label-head-org">Type:</label>
                                                <select
                                                    name="type"
                                                    value={formik.values.type}
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                    className="tabulate-select"
                                                    style={{ border: "1px solid #797979" }}
                                                >
                                                    <option hidden>Select a type</option>
                                                    <option value="adjudicator">Adjudicator</option>
                                                    <option value="musician">Musician</option>
                                                    <option value="sponsor">Sponsor</option>
                                                    <option value="examiner">Examiner</option>
                                                </select>
                                                {formik.touched.type && formik.errors.type && (
                                                    <span className="error mt-2">{formik.errors.type}</span>
                                                )}
                                            </div>
                                            {formik.values.type === "adjudicator" && (emailLocked
                                                ? <div className="d-grid mb-3">
                                                    <label className="label-head-org">Email:</label>
                                                    <span style={{ fontSize: "13px", color: "#797979" }}>{editCollab?.email} (account set up; the adjudicator manages it)</span>
                                                </div>
                                                : <CustomInput label="Email" name="email" type="email" placeholder="name@example.com" formik={formik} />)}
                                            {formik.values.type === "adjudicator" && <JudgeRegionField formik={formik} collab={editCollab} />}
                                            <input type="file" ref={imgInputRef} style={{ display: 'none' }} onChange={handleFileSelect} />

                                            <div className="d-grid">
                                                <label className="label-head-org" style={{ marginLeft: '1px' }} >Photo:</label>
                                                {logo && <img src={logo} alt="collab" style={{ width: "50px", height: "50px" }} className="mb-1" />}
                                                {logo ?
                                                    <div>
                                                        <button onClick={deleteLogoHandler} className="outlined mt-0">Delete</button>
                                                    </div>
                                                    : <div>
                                                        <AddButton onClick={handleLOgo} className="plusIconbtn mt-0 mb-2">Add Photo</AddButton>
                                                    </div>}

                                                {imgError && (<span className="error mt-2" style={{ width: '248px' }}>{imgError}</span>)}
                                                {upl && <span className="auth-suc">{upl}</span>}
                                            </div>
                                            <div className="mb-2 d-flex justify-content-center">
                                                <button 
                                                    className={(!formik.isValid || (!formik.dirty && !photoEdited)) ? 'disabled' : 'filled'}
                                                    style={{ fontWeight: '500', height: '42px', padding: '9px 11px', }}
                                                    type="submit"
                                                    disabled={!formik.isValid}
                                                >
                                                    {buttonName}
                                                </button>
                                            </div>
                                        </Form>
                                    )}
                                </Formik>
                            </div>
                        </div>
                    </div>
                </Box>
            </Modal>
        </>
    );
};


export default AddCollabModal